/**
 * MoveDragGhost — the pointer-following preview of a CROSS-GRID move (widget
 * between sections, section between pages). Counterpart of AddDragGhost
 * (palette drag-to-add): the grid HIDES the source item the moment its drop
 * preview lands in another grid (`draggedAway` → visibility:hidden) and
 * expects the app to mount `CrossDragGhost` as the traveling visual — without
 * this mount the item simply vanishes mid-drag.
 *
 * The library half (`CrossDragGhost`) owns the fixed positioning, grab-offset
 * math, transient pointer-follow and the group blueprint; the app half here
 * owns the chrome. Two app decisions:
 *
 * 1. RENDER THE REAL ITEM, card and all — the ghost shows the widget's actual
 *    content on its white card (via the preview branch of the same catalog
 *    the grids render with), not a translucent wash. What you grabbed is what
 *    travels.
 * 2. HIDE while the drop preview is still in the SOURCE grid — there the real
 *    item follows the pointer natively, and a ghost stacked on top reads as a
 *    double-render (the "blue veil over my widget" bug). The ghost takes over
 *    exactly when the grid hides the source item (preview in another grid).
 *
 * MOUNT CONTRACT (from the grid): render OUTSIDE any transformed/contained
 * ancestor — grid items and xyflow page nodes are containing blocks for
 * position:fixed. EditorScreen mounts it beside AddDragGhost, outside the
 * SitemapCanvas.
 */
import {
  Component,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { css, html } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { radii, spacing } from "@duro-app/tokens/tokens/spacing.css";
import {
  CrossDragGhost,
  DefaultGhost,
  useGridStore,
  type CrossDragGhostRenderArgs,
} from "@fredericrous/grid";

import { resolveWidget } from "./widgets/catalog.js";
import { cloneItemNode } from "./cloneItemNode.js";
import { useThemeVars } from "./ThemeVarsContext.js";

const s = css.create({
  boundaryFallback: {
    position: "absolute",
    top: spacing.sm,
    right: spacing.sm,
    bottom: spacing.sm,
    left: spacing.sm,
    backgroundColor: colors.bgCardHover,
    borderRadius: radii.xs,
  },
  refusedWash: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: colors.errorBg,
    borderWidth: borders.strong,
    borderStyle: "solid",
    borderColor: colors.error,
    borderRadius: radii.px6,
  },
  groupWrap: {
    width: "100%",
    height: "100%",
  },
  hidden: {
    visibility: "hidden",
  },
  visible: {
    visibility: "visible",
  },
  card: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    // The card mirrors the canvas item's own card, which is white whatever
    // the editor theme.
    backgroundColor: colors.fixedLight,
    borderWidth: borders.hairline,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.px6,
    overflow: "hidden",
    boxSizing: "border-box",
    // Shadow lives HERE, not on the library wrapper — the wrapper stays
    // mounted while the ghost hides over the source grid, and a floating
    // shadow with no card reads as a glitch.
    boxShadow: shadows.lg,
  },
  inner: (width: number, height: number, scale: number) => ({
    width,
    height,
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    backgroundColor: `var(--wb-color-background, ${colors.fixedLight})`,
    color: "var(--wb-color-foreground, inherit)",
    fontFamily: "var(--wb-font-body, inherit)",
  }),
  cloneHost: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
});

/**
 * Some catalog widgets assume the canvas context tree (NavWidget throws
 * outside EditorProvider), and the ghost mounts OUTSIDE the canvas per the
 * grid's mount contract. A widget that can't render as ghost content must
 * degrade to a neutral block — not take down the whole editor route through
 * the router's error boundary (observed: dragging the header nav unmounted
 * the entire canvas).
 */
class GhostContentBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override render(): ReactNode {
    if (this.state.failed) {
      return <html.div style={s.boundaryFallback} />;
    }
    return this.props.children;
  }
}

function MoveGhostContent(args: CrossDragGhostRenderArgs) {
  const { itemId, fromGridId, width, height, members, refused } = args;
  // Hidden ONLY while the pointer hovers the SOURCE grid — there the real
  // item follows the pointer natively and a ghost on top double-renders (the
  // blue-veil bug). Everywhere else the ghost is the traveling visual,
  // INCLUDING over no grid at all (overGridId null, e.g. past the window
  // edge): there the preview parks, so the real item either freezes at its
  // last slot (within-source) or stays hidden entirely (preview parked in
  // another grid) — hiding the ghost too made the drag vanish/freeze at
  // window edges (user report 2026-07-17). The subscription is per-frame
  // cheap: a boolean selector re-renders only on the handoff transition.
  const away = useGridStore((s) => {
    const cg = s.interaction.crossGrid;
    return !!cg && cg.overGridId !== cg.fromGridId;
  });
  const item = useGridStore((s) => s.grids[fromGridId]?.items[itemId]);
  const Widget = item ? resolveWidget(item.contentType) : null;

  // WHAT-YOU-GRABBED-IS-WHAT-TRAVELS: clone the item's REAL on-canvas DOM into
  // the card. Re-rendering the widget through the preview pipeline drifted
  // from the live item (different font-fallback stacks: the canvas Lexical
  // text resolved serif, the preview HTML sans — user report 2026-07-16); a
  // DOM clone is pixel-identical by construction. Cloned ONCE per gesture
  // (content is frozen mid-drag); the source Box's visibility:hidden is
  // INLINE ON THE BOX, so the cloned content div is unaffected. Falls back to
  // the catalog preview render when the node isn't in the DOM.
  const cloneHostRef = useRef<HTMLDivElement | null>(null);
  // Layout size of the cloned content (CSS px, pre-canvas-zoom). The grab box
  // (args.width/height) is SCREEN px — at canvas zoom ≠ 1 the two differ, and
  // rendering the clone into the screen-sized box changes its proportions
  // (28px text wraps differently in a 600px box than the 1060px it was laid
  // out for). Render at layout size and fold the zoom into the scale instead.
  const [cloneLayout, setCloneLayout] = useState<{
    w: number;
    h: number;
  } | null>(null);
  useEffect(() => {
    const host = cloneHostRef.current;
    if (!host) return;
    // Shared sync helper (also feeds the native drag's setDragImage): clones
    // the item chrome's content div — white card, the visuals — made inert.
    const clone = cloneItemNode(itemId);
    if (!clone) return;
    host.replaceChildren(clone.node);
    setCloneLayout({ w: clone.w, h: clone.h });
    return () => {
      host.replaceChildren();
      setCloneLayout(null);
    };
  }, [itemId]);
  const hasClone = cloneLayout != null;
  // The item inherits its typography from PageNode's inline theme context
  // (`--wb-*` vars + font-family var(--wb-font-body)) — the ghost mounts
  // outside the page nodes, so the DOM clone would fall back to app-default
  // fonts (serif on canvas vs sans in the ghost, user report 2026-07-16).
  // Re-establish the exact same context on the card.
  const themeVars = useThemeVars();
  // The `--wb-*` names are data (the site theme's keys), so they can't be
  // static css.create keys: set them on the inner card imperatively.
  const themeHostRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = themeHostRef.current;
    if (!el) return;
    const keys = Object.keys(themeVars);
    for (const k of keys) el.style.setProperty(k, themeVars[k] ?? "");
    return () => {
      for (const k of keys) el.style.removeProperty(k);
    };
  });

  // FULL SIZE, exact look: what travels is the item, as-is — the user grabs
  // a tile and that tile follows the cursor (the clipping that once made a
  // full-size visual problematic is fixed at the frames). Hidden only while
  // hovering the source grid: there the REAL item follows the pointer
  // natively and the ghost on top would double-render.
  const visible = away;
  // With a clone: render at the item's LAYOUT size and fold the canvas zoom
  // into the scale, so the card is a uniform photo of the on-screen item
  // (28px text into a zoomed-out 600px box would re-wrap otherwise).
  const innerW = cloneLayout?.w ?? width;
  const innerH = cloneLayout?.h ?? height;
  const innerScale = cloneLayout ? width / cloneLayout.w : 1;

  // Red is reserved for HARD violations — moves that no release can ever
  // perform (a pinned shared-region default dragged out of its band). A mere
  // "no valid target under the cursor" hover is NOT red: release simply
  // lands at the last valid slot (the parked landing placeholder already
  // shows where), and painting that case red read as "this will fail".
  const hardRefused = refused && itemId.startsWith("__");
  const wash = hardRefused ? (
    <html.div data-testid="move-ghost-refused" style={s.refusedWash} />
  ) : null;

  // Group blueprint (multi-select drag) keeps the library's member boxes —
  // per-member content rendering isn't worth the weight for that rare path.
  if (members && members.length > 0) {
    return (
      <html.div style={[s.groupWrap, visible ? s.visible : s.hidden]}>
        <DefaultGhost {...args} />
      </html.div>
    );
  }

  return (
    // The library wrapper is the tile's FULL grab-time box; the visible card
    // is the (possibly scaled) inner div, offset so the cursor keeps pointing
    // at the same relative spot of the card: the cursor sits at (grabDX,
    // grabDY) in the wrapper, so anchoring the mini card at grab·(1−scale)
    // pins that point through the shrink.
    <html.div
      data-testid="move-ghost-card"
      style={[s.card, visible ? s.visible : s.hidden]}
    >
      <html.div ref={themeHostRef} style={s.inner(innerW, innerH, innerScale)}>
        <html.div
          ref={cloneHostRef}
          data-testid="move-ghost-clone-host"
          // Overlay, not flow: when the clone is absent this must not push
          // the fallback render out of the card.
          style={s.cloneHost}
        />
        {!hasClone &&
          (item && Widget ? (
            <GhostContentBoundary>
              <Widget
                itemId={itemId}
                contentType={item.contentType}
                contentProps={{ ...(item.contentProps ?? {}), preview: true }}
                gridId={fromGridId}
              />
            </GhostContentBoundary>
          ) : (
            <DefaultGhost {...args} />
          ))}
      </html.div>
      {wash}
    </html.div>
  );
}

export function MoveDragGhost() {
  return (
    <CrossDragGhost
      zIndex={2000}
      renderGhost={(args) => <MoveGhostContent {...args} />}
      style={{ opacity: 0.95 }}
    />
  );
}
